import React from 'react'
import type { NextPage, GetServerSidePropsContext } from 'next'
import { useTranslations } from 'next-intl'
import { Box, useColorModeValue, Text, Link } from '@chakra-ui/react'
import { InfoFrame } from '@/components/InfoFrame'
import { PageHero } from '@/components/PageHero'
import { Highlights } from '@/components/Highlights'
import { VerticalSlideshow } from '@/components/VerticalSlideshow'
import { ContainerBase } from '@/components/containers/ContainerBase'
import { SectionWithCards } from '@/components/SectionWithCards'
import { HashboardIcon } from '@/components/icons/HashboardIcon'
import { FanIcon } from '@/components/icons/FanIcon'
import { SocketIcon } from '@/components/icons/SocketIcon'
import { ControlBoardsIcon } from '@/components/icons/ControlBoardsIcon'
import { CALENDLY_LINK, SUPPORT_REPAIRS_PRICES_URL, SUPPORT_REPAIRS_URL } from '@/constants'
import { TalktoSales } from '@/features/home/TalktoSales'
import { HighlightItem } from '@/types/highlights.interface'
import { ISlide } from '@/types/strapi/commonComponents.interface'
import { ITalktoSales } from '@/types/strapi/homePage.interface'
import SeoHead from '@/components/seo-head'
import type { CardData } from '@/components/SectionWithCards'

const RepairsPage: NextPage = () => {
  const t = useTranslations()
  const bg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100')

  const items: HighlightItem[] = [
    {
      icon: HashboardIcon,
      title: t('common.labels.hash-boards'),
      description: t('repairs.core-components'),
    },
    {
      icon: FanIcon,
      title: t('common.labels.fans'),
      description: t('repairs.critical-for-cooling'),
    },
    {
      icon: SocketIcon,
      title: t('common.labels.power-supply-units'),
      description: t('repairs.prone-to-failure'),
    },
    {
      icon: ControlBoardsIcon,
      title: t('common.labels.control-boards'),
      description: t('repairs.manage-overall'),
    },
  ]

  const operationalSlideshow: ISlide[] = [
    {
      id: t('repairs.continuous-operation'),
      title: t('repairs.continuous-operation'),
      description: t('repairs.run-without-pause'),
      image: { url: '/img/repairs-slide1.gif' },
      imageMobile: { url: '/img/repairs-slide1.gif' },
    },
    {
      id: t('repairs.intense-heat-generation'),
      title: t('repairs.intense-heat-generation'),
      description: t('repairs.computational-power'),
      image: { url: '/img/repairs-slide2.gif' },
      imageMobile: { url: '/img/repairs-slide2.gif' },
    },
    {
      id: t('repairs.dust-environmental-factors'),
      title: t('repairs.dust-environmental-factors'),
      description: t('repairs.dust-accumulation'),
      image: { url: '/img/repairs-slide3.gif' },
      imageMobile: { url: '/img/repairs-slide3.gif' },
    },
  ]

  const miningProblems: CardData[] = [
    {
      title: t('repairs.mining-pushes-limits'),
      description: t('repairs.hashrate-pursuit'),
      iconImage: { url: '/img/repairs-bucket.jpg' },
      objectFit: 'cover',
    },
    {
      title: t('repairs.repairs-process'),
      description: t('repairs.robust-modular-design'),
      iconImage: { url: '/img/repair-part.jpg' },
      iconImageMobile: { url: '/img/repair-part-mobile.png' },
      objectFit: 'cover',
    },
  ]

  const repairServices: CardData[] = [
    {
      title: t('repairs.lightning-time'),
      description: t('repairs.fully-stocked-facilities'),
      iconImage: { url: '/img/technician-working.jpg' },
      iconImageMobile: { url: '/img/technician-working-mobile.jpg' },
      objectFit: 'cover',
    },
    {
      title: t('repairs.world-class-technicians'),
      description: t('repairs.complex-board-level'),
      iconImage: { url: '/img/control-board-fix.jpg' },
      iconImageMobile: { url: '/img/control-board-fix-mobile.jpg' },
      objectFit: 'cover',
    },
    {
      title: t('repairs.track-repairs'),
      description: t('repairs.centralized-hub'),
      iconImage: { url: '/img/repairs-page.png' },
      iconImageMobile: { url: '/img/repairs-page-mobile.png' },
      objectFit: 'cover',
      url: SUPPORT_REPAIRS_PRICES_URL,
      urlLabel: t('buttons.learn-more')
    },
    {
      title: t('repairs.competitive-pricing'),
      description: t('repairs.competitive-repair-pricing'),
      iconImage: { url: '/img/competitive-pricing.jpg' },
      iconImageMobile: { url: '/img/competitive-pricing-mobile.jpg' },
      objectFit: 'cover',
      url: SUPPORT_REPAIRS_PRICES_URL,
      urlLabel: t('common.learn-about-pricing')
    },
  ]

  const talkToSalesData: ITalktoSales[] = [
    {
      subtitle: t('common.labels.need-help'),
      title: t('common.personalized-guidance'),
      description: t('common.starting-out'),
      button: {
        id: 'talk-to-sales-cta',
        text: t('contact-sales.schedule-a-call'),
        url: CALENDLY_LINK,
        newTab: false,
        iconName: null,
      },
    },
  ]

  return (
    <>
      <SeoHead
        title={t('repairs.seo.title')}
        breadcrumbTitle={t('repairs.seo.breadcrumb')}
        description={t('repairs.seo.description')}
      />
      <PageHero
        title={t('repairs.keep-miners-running')}
        subTitle={t('repairs.repair-services')}
        description={t('repairs.get-miners-online')}
        image="img/repairs-hero.png"
        mobileImage="img/repairs-hero.png"
        anchor="what-are-repair-services"
      />
      <Box id="what-are-repair-services">
        <InfoFrame
          title={t('repairs.what-are-repair-services')}
          description={t('repairs.comprehensive-repair-services')}
        />
      </Box>
      <Box bg={bg}>
        <ContainerBase
          px={{ base: '32px', md: 4 }}
          py={{ base: '42px', md: '100px' }}
        >
          <SectionWithCards
            title={t('repairs.integral-repairs')}
            subtitle={t('repairs.easy-upgrade')}
            cards={miningProblems}
            cardImageOverlay={false}
            cardImageBg="gray.890"
            cardContentPy={8}
            cardBg="black"
            cardTitleColor="white"
            cardDescriptionColor="whiteAlpha.800"
            cardBorderColor="whiteAlpha.500"
            cardImageHeight="252px"
            alignStart
            columns={2}
          />
        </ContainerBase>
      </Box>
      <ContainerBase px={{ base: '32px', md: 4 }}>
        <Box py={{ base: '64px', md: '100px' }}>
          <VerticalSlideshow
            title={t('repairs.harsh-environment')}
            subtitle={t('repairs.run-nonstop')}
            slideshow={operationalSlideshow}
            reverseLayout
            alignStart
          />
        </Box>
      </ContainerBase>
      <Box bg={bg}>
        <Highlights
          title={t('hosting-orders.common-points-failure')}
          description={t('hosting-orders.frequent-miner-failures')}
          items={items}
          columns={2}
        />
      </Box>
      <InfoFrame
        title={t('repairs.economic-impact')}
        description={t('repairs.preventative-maintenance')}
        reverseColors
      />
      <Box bg={bg}>
        <ContainerBase
          px={{ base: '32px', md: 4 }}
          py={{ base: '42px', md: '100px' }}
        >
          <SectionWithCards
            title={t('repairs.fast-reliable-repairs')}
            subtitle={t('repairs.quick-part-swaps')}
            cards={repairServices}
            cardImageOverlay={false}
            cardImageBg="gray.890"
            cardContentPy={8}
            cardBg="black"
            cardTitleColor="white"
            cardDescriptionColor="whiteAlpha.800"
            cardBorderColor="whiteAlpha.500"
            cardImageHeight="252px"
            alignStart
            columns={2}
          />
          <Text
            variant="spaceGrotesk"
            fontSize={{ base: '16px', md: '24px' }}
            fontWeight="300"
          >
            {t.rich('repairs.repair-faq', {
              link: (children) => (
                <Link href={SUPPORT_REPAIRS_URL}>{children}</Link>
              ),
            })}
          </Text>
        </ContainerBase>
      </Box>
      <TalktoSales talkToSales={talkToSalesData || []} />
    </>
  )
}

export async function getStaticProps({ locale }: GetServerSidePropsContext) {
  return {
    props: {
      messages: (await import(`../../../locales/${locale}.json`)).default,
    },
  }
}

export default RepairsPage
